import type {
  INeoTicketService,
  NeoTicketError,
} from "../ports/driver/neoTicketService";
import type {
  INeoTicketRepository,
  NeoTicketBlock,
  NeoTicketCreateRequest,
  NeoTicketListOptions,
  NeoTicketPatchRequest,
} from "../ports/driven/neoTicketRepository";

const toError = (error: unknown): NeoTicketError => ({
  message: error instanceof Error ? error.message : "Something went wrong.",
});

export const NeoTicketServiceImplFactory = ({
  neoTicketRepository,
}: {
  neoTicketRepository: INeoTicketRepository;
}): INeoTicketService => {
  const repo = neoTicketRepository;

  const listTickets = async (
    projectId: number,
    opts?: NeoTicketListOptions
  ) => {
    try {
      const resp = await repo.listTicketsAPI(projectId, opts);
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const getTicket = async (projectId: number, ticketId: number) => {
    try {
      const data = await repo.getTicketAPI(projectId, ticketId);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const createTicket = async (
    projectId: number,
    payload: NeoTicketCreateRequest
  ) => {
    try {
      const data = await repo.createTicketAPI(projectId, payload);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const updateTicket = async (
    projectId: number,
    ticketId: number,
    payload: NeoTicketPatchRequest
  ) => {
    try {
      const data = await repo.updateTicketAPI(projectId, ticketId, payload);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const appendTicketBlocks = async (
    projectId: number,
    ticketId: number,
    blocks: NeoTicketBlock[]
  ) => {
    try {
      const data = await repo.appendTicketBlocksAPI(
        projectId,
        ticketId,
        blocks
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const cancelTicket = async (projectId: number, ticketId: number) => {
    try {
      const data = await repo.cancelTicketAPI(projectId, ticketId);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const deleteTicket = async (projectId: number, ticketId: number) => {
    try {
      await repo.deleteTicketAPI(projectId, ticketId);
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  return {
    listTickets,
    getTicket,
    createTicket,
    updateTicket,
    appendTicketBlocks,
    cancelTicket,
    deleteTicket,
  };
};
